import React, { useState, useEffect } from 'react';
import {
  FiSave, FiGlobe, FiMail, FiPhone, FiMapPin,
  FiCreditCard, FiTruck, FiSettings, FiPercent,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import merchantService from '../../services/merchant.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const MerchantStoreSettingsPage = () => {
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [form, setForm] = useState({
    storeName: '',
    description: '',
    logo: '',
    banner: '',
    branding: {
      primaryColor: '#B91C1C',
      secondaryColor: '#DC2626',
      accentColor: '#EAB308',
      fontFamily: 'Inter',
      theme: 'light',
      customCSS: '',
    },
    contact: {
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      },
    },
    socialLinks: {
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: '',
    },
    businessInfo: {
      legalName: '',
      registrationNumber: '',
      taxId: '',
      businessType: 'individual',
      returnPolicy: '',
    },
    paymentConfig: {
      stripeAccountId: '',
      razorpayAccountId: '',
      preferredGateway: 'stripe',
      commissionRate: 5,
    },
    shippingConfig: {
      freeShippingThreshold: 50,
      defaultShippingCost: 0,
    },
    settings: {
      currency: 'USD',
      taxEnabled: false,
      taxRate: 0,
      allowGuestCheckout: true,
    },
    categories: [],
  });

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const { data } = await merchantService.getMyStore();
        const s = data.data;
        setStore(s);
        setForm({
          storeName: s.storeName || '',
          description: s.description || '',
          logo: s.logo || '',
          banner: s.banner || '',
          branding: {
            primaryColor: s.branding?.primaryColor || '#B91C1C',
            secondaryColor: s.branding?.secondaryColor || '#DC2626',
            accentColor: s.branding?.accentColor || '#EAB308',
            fontFamily: s.branding?.fontFamily || 'Inter',
            theme: s.branding?.theme || 'light',
            customCSS: s.branding?.customCSS || '',
          },
          contact: {
            email: s.contact?.email || '',
            phone: s.contact?.phone || '',
            address: {
              street: s.contact?.address?.street || (typeof s.contact?.address === 'string' ? s.contact.address : ''),
              city: s.contact?.address?.city || '',
              state: s.contact?.address?.state || '',
              zip: s.contact?.address?.zip || '',
              country: s.contact?.address?.country || '',
            },
          },
          socialLinks: {
            website: s.socialLinks?.website || s.contact?.website || '',
            facebook: s.socialLinks?.facebook || '',
            instagram: s.socialLinks?.instagram || '',
            twitter: s.socialLinks?.twitter || '',
            youtube: s.socialLinks?.youtube || '',
          },
          businessInfo: {
            legalName: s.businessInfo?.legalName || '',
            registrationNumber: s.businessInfo?.registrationNumber || '',
            taxId: s.businessInfo?.taxId || '',
            businessType: s.businessInfo?.businessType || 'individual',
            returnPolicy: s.businessInfo?.returnPolicy || '',
          },
          paymentConfig: {
            stripeAccountId: s.paymentConfig?.stripeAccountId || '',
            razorpayAccountId: s.paymentConfig?.razorpayAccountId || '',
            preferredGateway: s.paymentConfig?.preferredGateway || 'stripe',
            commissionRate: s.paymentConfig?.commissionRate ?? 5,
          },
          shippingConfig: {
            freeShippingThreshold: s.shippingConfig?.freeShippingThreshold ?? 50,
            defaultShippingCost: s.shippingConfig?.defaultShippingCost ?? 0,
          },
          settings: {
            currency: s.settings?.currency || 'USD',
            taxEnabled: s.settings?.taxEnabled ?? false,
            taxRate: s.settings?.taxRate ?? 0,
            allowGuestCheckout: s.settings?.allowGuestCheckout ?? true,
          },
          categories: s.categories || [],
        });
      } catch (error) {
        toast.error('Failed to load store settings');
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await merchantService.updateMyStore(form);
      setStore(data.data);
      toast.success('Store settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    if (section) {
      setForm((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const updateAddress = (field, value) => {
    setForm((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        address: { ...prev.contact.address, [field]: value },
      },
    }));
  };

  if (loading) return <LoadingSpinner />;

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'branding', label: 'Branding' },
    { key: 'contact', label: 'Contact & Social' },
    { key: 'business', label: 'Business Info' },
    { key: 'payment', label: 'Payments' },
    { key: 'shipping', label: 'Shipping' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div>
      <div style={{ maxWidth: '900px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Store Settings</h2>
            {store?.storeSlug && (
              <p className="text-muted mb-0">
                Store URL: /stores/{store.storeSlug}
              </p>
            )}
          </div>
          <button className="btn btn-ebw-primary" onClick={handleSave} disabled={saving}>
            <FiSave className="me-1" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-0" style={{ flexWrap: 'wrap' }}>
          {tabs.map((tab) => (
            <li key={tab.key} className="nav-item">
              <button
                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="dashboard-card rounded-top-0 border-top-0">

          {/* ===== GENERAL ===== */}
          {activeTab === 'general' && (
            <div>
              <div className="mb-3">
                <label className="form-label">Store Name *</label>
                <input
                  type="text" className="form-control"
                  value={form.storeName}
                  onChange={(e) => updateField(null, 'storeName', e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Store Description</label>
                <textarea
                  className="form-control" rows={4}
                  value={form.description}
                  onChange={(e) => updateField(null, 'description', e.target.value)}
                  placeholder="Tell customers about your store..."
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Logo URL</label>
                <input
                  type="url" className="form-control"
                  value={form.logo}
                  onChange={(e) => updateField(null, 'logo', e.target.value)}
                  placeholder="https://..."
                />
                {form.logo && (
                  <img src={form.logo} alt="Logo" className="mt-2 rounded border" style={{ maxHeight: '60px' }} />
                )}
              </div>
              <div className="mb-0">
                <label className="form-label">Banner Image URL</label>
                <input
                  type="url" className="form-control"
                  value={form.banner}
                  onChange={(e) => updateField(null, 'banner', e.target.value)}
                  placeholder="https://..."
                />
                {form.banner && (
                  <img src={form.banner} alt="Banner" className="mt-2 rounded border w-100" style={{ maxHeight: '120px', objectFit: 'cover' }} />
                )}
              </div>
            </div>
          )}

          {/* ===== BRANDING ===== */}
          {activeTab === 'branding' && (
            <div>
              <div className="row g-3 mb-3">
                {[
                  { key: 'primaryColor', label: 'Primary Color' },
                  { key: 'secondaryColor', label: 'Secondary Color' },
                  { key: 'accentColor', label: 'Accent Color' },
                ].map(({ key, label }) => (
                  <div className="col-md-4" key={key}>
                    <label className="form-label">{label}</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color" className="form-control form-control-color"
                        value={form.branding[key]}
                        onChange={(e) => updateField('branding', key, e.target.value)}
                      />
                      <input
                        type="text" className="form-control"
                        value={form.branding[key]}
                        onChange={(e) => updateField('branding', key, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Font Family</label>
                  <select
                    className="form-select"
                    value={form.branding.fontFamily}
                    onChange={(e) => updateField('branding', 'fontFamily', e.target.value)}
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Playfair Display">Playfair Display</option>
                    <option value="Lato">Lato</option>
                    <option value="Montserrat">Montserrat</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Theme</label>
                  <select
                    className="form-select"
                    value={form.branding.theme}
                    onChange={(e) => updateField('branding', 'theme', e.target.value)}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              {form.branding.theme === 'custom' && (
                <div className="mb-3">
                  <label className="form-label">Custom CSS</label>
                  <textarea
                    className="form-control font-monospace"
                    rows={5}
                    value={form.branding.customCSS}
                    onChange={(e) => updateField('branding', 'customCSS', e.target.value)}
                    placeholder=".store-header { background: #fff; }&#10;.product-card { border-radius: 12px; }"
                    style={{ fontSize: '0.85rem' }}
                  />
                  <small className="text-muted">CSS applied to your store page. Use with caution.</small>
                </div>
              )}

              {/* Preview */}
              <div className="p-3 rounded" style={{ background: form.branding.primaryColor + '15' }}>
                <h6 style={{ color: form.branding.primaryColor, fontFamily: form.branding.fontFamily }}>
                  Preview: {form.storeName || 'Your Store'}
                </h6>
                <div className="d-flex gap-2 flex-wrap">
                  <button className="btn btn-sm" style={{ background: form.branding.primaryColor, color: '#fff' }}>
                    Primary
                  </button>
                  <button className="btn btn-sm" style={{ background: form.branding.secondaryColor, color: '#fff' }}>
                    Secondary
                  </button>
                  <button className="btn btn-sm" style={{ background: form.branding.accentColor, color: '#fff' }}>
                    Accent
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== CONTACT & SOCIAL ===== */}
          {activeTab === 'contact' && (
            <div>
              <h6 className="fw-bold mb-3">Contact Information</h6>
              <div className="mb-3">
                <label className="form-label"><FiMail className="me-1" /> Email</label>
                <input type="email" className="form-control" value={form.contact.email}
                  onChange={(e) => updateField('contact', 'email', e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label"><FiPhone className="me-1" /> Phone</label>
                <input type="tel" className="form-control" value={form.contact.phone}
                  onChange={(e) => updateField('contact', 'phone', e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label"><FiMapPin className="me-1" /> Address</label>
                <input type="text" className="form-control mb-2" placeholder="Street"
                  value={form.contact.address.street}
                  onChange={(e) => updateAddress('street', e.target.value)} />
                <div className="row g-2">
                  <div className="col-md-4">
                    <input type="text" className="form-control" placeholder="City"
                      value={form.contact.address.city}
                      onChange={(e) => updateAddress('city', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="State"
                      value={form.contact.address.state}
                      onChange={(e) => updateAddress('state', e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <input type="text" className="form-control" placeholder="ZIP"
                      value={form.contact.address.zip}
                      onChange={(e) => updateAddress('zip', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Country"
                      value={form.contact.address.country}
                      onChange={(e) => updateAddress('country', e.target.value)} />
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              <h6 className="fw-bold mb-3">Social & Web Links</h6>
              <div className="mb-3">
                <label className="form-label"><FiGlobe className="me-1" /> Website</label>
                <input type="url" className="form-control" value={form.socialLinks.website}
                  onChange={(e) => updateField('socialLinks', 'website', e.target.value)}
                  placeholder="https://yoursite.com" />
              </div>
              {['facebook', 'instagram', 'twitter', 'youtube'].map((platform) => (
                <div key={platform} className="mb-3">
                  <label className="form-label text-capitalize">{platform}</label>
                  <input
                    type="url" className="form-control"
                    placeholder={`https://${platform}.com/...`}
                    value={form.socialLinks[platform]}
                    onChange={(e) => updateField('socialLinks', platform, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ===== BUSINESS INFO ===== */}
          {activeTab === 'business' && (
            <div>
              <div className="mb-3">
                <label className="form-label">Legal Business Name</label>
                <input type="text" className="form-control" value={form.businessInfo.legalName}
                  onChange={(e) => updateField('businessInfo', 'legalName', e.target.value)} />
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Business Type</label>
                  <select
                    className="form-select"
                    value={form.businessInfo.businessType}
                    onChange={(e) => updateField('businessInfo', 'businessType', e.target.value)}
                  >
                    <option value="individual">Individual / Sole Proprietor</option>
                    <option value="company">Company / Corporation</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Registration Number</label>
                  <input type="text" className="form-control"
                    value={form.businessInfo.registrationNumber}
                    onChange={(e) => updateField('businessInfo', 'registrationNumber', e.target.value)}
                    placeholder="Business registration / CIN" />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Tax ID / GST Number</label>
                <input type="text" className="form-control" value={form.businessInfo.taxId}
                  onChange={(e) => updateField('businessInfo', 'taxId', e.target.value)}
                  placeholder="e.g. 22AAAAA0000A1Z5" />
              </div>
              <div className="mb-0">
                <label className="form-label">Return Policy</label>
                <textarea className="form-control" rows={4} value={form.businessInfo.returnPolicy}
                  onChange={(e) => updateField('businessInfo', 'returnPolicy', e.target.value)}
                  placeholder="Describe your return/refund policy..." />
              </div>
            </div>
          )}

          {/* ===== PAYMENTS ===== */}
          {activeTab === 'payment' && (
            <div>
              <div className="mb-3">
                <label className="form-label"><FiCreditCard className="me-1" /> Preferred Payment Gateway</label>
                <select
                  className="form-select"
                  value={form.paymentConfig.preferredGateway}
                  onChange={(e) => updateField('paymentConfig', 'preferredGateway', e.target.value)}
                >
                  <option value="stripe">Stripe</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Stripe Account ID</label>
                <input type="text" className="form-control"
                  value={form.paymentConfig.stripeAccountId}
                  onChange={(e) => updateField('paymentConfig', 'stripeAccountId', e.target.value)}
                  placeholder="acct_..." />
                <small className="text-muted">Your Stripe Connect account ID for receiving payouts.</small>
              </div>
              <div className="mb-3">
                <label className="form-label">Razorpay Account ID</label>
                <input type="text" className="form-control"
                  value={form.paymentConfig.razorpayAccountId}
                  onChange={(e) => updateField('paymentConfig', 'razorpayAccountId', e.target.value)}
                  placeholder="acc_..." />
                <small className="text-muted">Your Razorpay linked account ID for receiving payouts.</small>
              </div>
              <div className="mb-0">
                <label className="form-label"><FiPercent className="me-1" /> Platform Commission Rate (%)</label>
                <input type="number" className="form-control" min="0" max="100" step="0.5"
                  value={form.paymentConfig.commissionRate}
                  onChange={(e) => updateField('paymentConfig', 'commissionRate', parseFloat(e.target.value) || 0)}
                  style={{ maxWidth: '200px' }} />
                <small className="text-muted">
                  Platform takes {form.paymentConfig.commissionRate}% commission on each sale.
                </small>
              </div>
            </div>
          )}

          {/* ===== SHIPPING ===== */}
          {activeTab === 'shipping' && (
            <div>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label"><FiTruck className="me-1" /> Default Shipping Cost</label>
                  <div className="input-group">
                    <span className="input-group-text">{form.settings.currency === 'INR' ? '₹' : '$'}</span>
                    <input
                      type="number" className="form-control" min="0" step="0.01"
                      value={form.shippingConfig.defaultShippingCost}
                      onChange={(e) => updateField('shippingConfig', 'defaultShippingCost', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <small className="text-muted">Standard shipping fee per order.</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Free Shipping Threshold</label>
                  <div className="input-group">
                    <span className="input-group-text">{form.settings.currency === 'INR' ? '₹' : '$'}</span>
                    <input
                      type="number" className="form-control" min="0" step="0.01"
                      value={form.shippingConfig.freeShippingThreshold}
                      onChange={(e) => updateField('shippingConfig', 'freeShippingThreshold', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <small className="text-muted">Orders above this amount get free shipping. Set to 0 for always free.</small>
                </div>
              </div>
            </div>
          )}

          {/* ===== SETTINGS ===== */}
          {activeTab === 'settings' && (
            <div>
              <div className="mb-3">
                <label className="form-label"><FiSettings className="me-1" /> Store Currency</label>
                <select
                  className="form-select"
                  value={form.settings.currency}
                  onChange={(e) => updateField('settings', 'currency', e.target.value)}
                  style={{ maxWidth: '300px' }}
                >
                  <option value="USD">USD — US Dollar ($)</option>
                  <option value="INR">INR — Indian Rupee (₹)</option>
                  <option value="EUR">EUR — Euro (€)</option>
                  <option value="GBP">GBP — British Pound (£)</option>
                </select>
              </div>

              <hr />

              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input" type="checkbox" role="switch"
                    id="taxEnabled"
                    checked={form.settings.taxEnabled}
                    onChange={(e) => updateField('settings', 'taxEnabled', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="taxEnabled">
                    Enable Tax
                  </label>
                </div>
                <small className="text-muted">Automatically calculate tax on orders.</small>
              </div>

              {form.settings.taxEnabled && (
                <div className="mb-3">
                  <label className="form-label">Tax Rate (%)</label>
                  <input type="number" className="form-control" min="0" max="100" step="0.25"
                    value={form.settings.taxRate}
                    onChange={(e) => updateField('settings', 'taxRate', parseFloat(e.target.value) || 0)}
                    style={{ maxWidth: '200px' }} />
                </div>
              )}

              <hr />

              <div className="mb-0">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input" type="checkbox" role="switch"
                    id="guestCheckout"
                    checked={form.settings.allowGuestCheckout}
                    onChange={(e) => updateField('settings', 'allowGuestCheckout', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="guestCheckout">
                    Allow Guest Checkout
                  </label>
                </div>
                <small className="text-muted">Let customers purchase without creating an account.</small>
              </div>
            </div>
          )}
        </div>

        {/* Save button at bottom */}
        <div className="text-end mt-3">
          <button className="btn btn-ebw-primary btn-lg" onClick={handleSave} disabled={saving}>
            <FiSave className="me-1" /> {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchantStoreSettingsPage;
